import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Container } from 'react-bootstrap';
import { useGeneric } from '../../../apis/generic/GenericProvider';
import AlertOverlay from '../../../components/Alert';
import {
  getErrorMessage,
  getUiInterface,
} from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { InsertFormProps, ReleasePayload } from '../types';
import { FaChevronDown } from "react-icons/fa";
import './InsertForm.css';

const InsertForm: React.FC<InsertFormProps> = ({ onSubmitForm }) => {
  const { t } = useTranslation();
  const { distinctOptions, fetchDistinctOptions } = useGeneric();
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'danger' | 'warning' | 'info';
  } | null>(null);

  const requiredFields = ['consignee', 'destination', 'shippedBy', 'freightType', 'to'];

  const [form, setForm] = useState<ReleasePayload>({
    consignee: '',
    destination: '',
    shippedBy: '',
    obs: '',
    freightType: '',
    to: '',
    tc: '4',
  });

  const [wareHousesByDestination, setWareHousesByDestination] = useState<any[]>([]);

  useEffect(() => {
    fetchDistinctOptions('clientWithBranches');
    fetchDistinctOptions('destination');
    fetchDistinctOptions('freightType');
  }, [fetchDistinctOptions]);

  // Fechar dropdown customizado ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('to-dropdown') as HTMLDivElement;
      const selectElement = event.target as HTMLElement;
      
      if (dropdown && 
          dropdown.style.display === 'block' && 
          !dropdown.contains(selectElement) &&
          !selectElement.closest('.custom-select-wrapper')) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [selectedFile, setSelectedFile] = useState<File[]>([]);

  // Função para buscar warehouses filtrados por destination
  const fetchWareHousesByDestination = async (destinationId: string) => {
    if (!destinationId) {
      console.log('[DEBUG] destinationId vazio, limpando warehouses');
      setWareHousesByDestination([]);
      return;
    }
    
    console.log(`[DEBUG] Buscando warehouses para destination: ${destinationId}`);
    
    try {
      const baseUrl = process.env.REACT_APP_BACK_END_API_URL;
      const url = `${baseUrl}WareHouse/by-destination/${destinationId}`;
      console.log(`[DEBUG] URL completa da requisição: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      console.log(`[DEBUG] Status da resposta: ${response.status}`);
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        console.log('[DEBUG] Content-Type:', contentType);
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log('[DEBUG] Dados recebidos:', data);
          
          // O backend retorna no formato { data: { value: [...] } }
          const warehouses = data.data?.value || data.value || data.data || [];
          console.log('[DEBUG] Warehouses processados:', warehouses);
          setWareHousesByDestination(warehouses);
        } else {
          const text = await response.text();
          console.error('[DEBUG] Resposta não é JSON:', text.substring(0, 200));
          setWareHousesByDestination([]);
        }
      } else {
        const text = await response.text();
        console.error(`[DEBUG] Erro ${response.status}: ${text.substring(0, 200)}`);
        setWareHousesByDestination([]);
      }
    } catch (error) {
      console.error('[DEBUG] Erro ao buscar warehouses:', error);
      setWareHousesByDestination([]);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    // Se a destination mudou, buscar warehouses filtrados
    if (name === 'destination' && value) {
      fetchWareHousesByDestination(value);
      // Limpar o campo 'to' quando destination mudar
      setForm((prev) => ({ ...prev, to: '' }));
    }

    if (value.trim() !== '') {
      setInvalidFields((prev) => {
        const updated = new Set(prev);
        updated.delete(name);
        return updated;
      });
    }
  };


  const validateForm = () => {
    const newInvalidFields = new Set<string>();

    requiredFields.forEach((field) => {
      if (!form[field as keyof typeof form]) {
        newInvalidFields.add(field);
      }
    });

    setInvalidFields(newInvalidFields);
    return newInvalidFields.size === 0;
  };


  const handleSubmit = () => {
    if (!validateForm()) {
      setAlert({ message: getUiInterface('fieldRequired'), variant: 'danger' });
      return;
    }

    onSubmitForm(form, selectedFile || []);
  };

  // Compartilhada pelo <input type="file"> e pelo drop de arquivos (E3): as
  // duas formas de escolher arquivo caem na mesma validação de tamanho e no
  // mesmo acúmulo em selectedFile.
  const processFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const validFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > maxFileSize) {
        setAlert({
          message: getErrorMessage('fileSize10MBError'),
          variant: 'danger',
        });
        return;
      }
      validFiles.push(file);
    }

    setSelectedFile((prev) => [...prev, ...validFiles]);

    setInvalidFields((prev) => {
      const updated = new Set(prev);
      updated.delete('selectedFile');
      return updated;
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
  };

  // E3: arraste de arquivos direto na área de upload da tela de liberação.
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    processFiles(e.dataTransfer.files);
  };

  const handleRemoveFile = (fileName: string) => {
    const fileList = selectedFile;
    const newList = fileList.filter((file) => file.name !== fileName);
    setSelectedFile(newList);
  };

  const selectOptions = {
    consignee: distinctOptions['clientWithBranches'] || distinctOptions['client'] || [],
    destination: distinctOptions['destination'] || [],
    shippedBy: ['air', 'ocean', 'road'],
    freightType: distinctOptions['freightType'] || [],
    to: wareHousesByDestination || [],
  };

  return (
    <Container fluid>
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}

      <Form className="mb-3">
        <Row className="mb-3">
          {Object.keys(form).filter(key => key !== 'tc').map((key) => (
            <Col md={4} key={key}>
              <Form.Group>
                <Form.Label>
                  {t(`release.fields.${key}`)}
                </Form.Label>

                {key === 'obs' ? (
                  <Form.Control
                    as="textarea"
                    placeholder={t('release.labels.enterObservations')}
                    name={key}
                    value={form[key as keyof typeof form]}
                    onChange={handleInputChange}
                  />
                ) : key === 'to' ? (
                  // Select customizado para o campo "Para" com scroll horizontal
                  <div className="custom-select-wrapper" style={{ position: "relative" }}>
                    <div 
                      className={`form-control ${invalidFields.has(key) ? 'is-invalid' : ''}`}
                      style={{
                        appearance: "none",
                        paddingRight: "30px",
                        backgroundImage: 'none',
                        cursor: !form.destination ? 'not-allowed' : 'pointer',
                        backgroundColor: !form.destination ? '#e9ecef' : 'white',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        if (form.destination) {
                          const dropdown = document.getElementById('to-dropdown') as HTMLDivElement;
                          if (dropdown) {
                            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                          }
                        }
                      }}
                    >
                      {form.to ? 
                        selectOptions.to.find(w => w.id === form.to)?.identification + 
                        ' - ' + 
                        selectOptions.to.find(w => w.id === form.to)?.name || 
                        t(`release.labels.select${key.charAt(0).toUpperCase() + key.slice(1)}`)
                        : t(`release.labels.select${key.charAt(0).toUpperCase() + key.slice(1)}`)
                      }
                    </div>
                    
                    {/* Dropdown customizado com scroll horizontal */}
                    <div 
                      id="to-dropdown"
                      style={{
                        display: 'none',
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ced4da',
                        borderTop: 'none',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}
                    >
                      <div 
                        style={{ padding: '8px', color: '#6c757d', cursor: 'default' }}
                        onClick={() => {
                          const dropdown = document.getElementById('to-dropdown') as HTMLDivElement;
                          dropdown.style.display = 'none';
                        }}
                      >
                        {t(`release.labels.select${key.charAt(0).toUpperCase() + key.slice(1)}`)}
                      </div>
                      {selectOptions[key as keyof typeof selectOptions]?.map((option) => {
                        if (
                          typeof option === "object" &&
                          "id" in option &&
                          "name" in option &&
                          "identification" in option
                        ) {
                          return (
                            <div
                              key={option.id}
                              style={{
                                padding: '8px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                minWidth: 'max-content',
                                borderBottom: '1px solid #f0f0f0'
                              }}
                              onClick={() => {
                                setForm(prev => ({ ...prev, [key]: option.id }));
                                const dropdown = document.getElementById('to-dropdown') as HTMLDivElement;
                                dropdown.style.display = 'none';
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              {option.identification} - {option.name}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                    <FaChevronDown
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        zIndex: 1001
                      }}
                    />
                  </div>
                ) : (
                  // Campos select normais
                  <div className="custom-select-wrapper" style={{ position: "relative" }}>
                    <Form.Control
                      as="select"
                      name={key}
                      value={form[key as keyof typeof form]}
                      onChange={handleInputChange}
                      isInvalid={invalidFields.has(key)}
                      style={{
                        appearance: "none",
                        paddingRight: "30px",
                        backgroundImage: 'none',
                      }}
                    >
                      <option value="">{t(`release.labels.select${key.charAt(0).toUpperCase() + key.slice(1)}`)}</option>
                      {selectOptions[key as keyof typeof selectOptions]?.map((option) => {
                        if (
                          typeof option === "object" &&
                          "id" in option &&
                          "description" in option
                        ) {
                          return (
                            <option key={option.id} value={option.id}>
                              {option.description}
                            </option>
                          );
                        }
                        if (
                          typeof option === "object" &&
                          "id" in option &&
                          "name" in option &&
                          "identification" in option
                        ) {
                          return (
                            <option key={option.id} value={option.id}>
                              {option.identification} - {option.name}
                            </option>
                          );
                        }
                        return (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <FaChevronDown
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
          ))}
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <Form.Group>
              <div
                className={`d-flex align-items-center flex-wrap p-2 release-dropzone${isDraggingFile ? ' release-dropzone-active' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Form.Control
                  id="file-upload-input"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                <div className="p-1 rounded">
                  <Button
                    as="label"
                    htmlFor="file-upload-input"
                    variant="secondary"
                    size="sm"
                    style={{ width: 150 }}
                  >
                    {getUiInterface('uploadFile')}
                  </Button>
                </div>

                <span className="ms-2 text-muted" style={{ fontSize: 12 }}>
                  {getUiInterface('orDragFilesHere')}
                </span>

                <div className="w-100 d-flex gap-2 mt-2">
                  {selectedFile.map((file) => (
                    <div
                      className="ms-2 d-flex align-items-center p-2 border rounded gap-2"
                      key={file.name}
                    >
                      <span className="me-2">
                        {file.name.length > 10
                          ? file.name.slice(0, 10) + '...'
                          : file.name}
                      </span>

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveFile(file.name)}
                      >
                        X
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" onClick={handleSubmit}>
          {getUiInterface('addRelease')}
        </Button>
      </Form>
    </Container>
  );
};

export default InsertForm;
